import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  DialogTitle,
  DialogContent
} from '@mui/material';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { useSnackBar } from '../../../../contexts/snackbar';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import { AddLink, Cancel, Check, CheckCircle, ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import { IWebhook } from '../../../../interfaces/common/webhooks';
import GenericTable from '../../../common/generic-table/generic-table';
import { IconWithText } from '../../../common/styles/components';
import { emptyCustomer } from '../../../../data/customers';
import { emptyWebhook } from '../../../../data/webhooks';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { isMobile, isTablet } from 'react-device-detect';
import WebhookForm from './webhook-form';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import { green, red } from '@mui/material/colors';

function WebhooksListing() {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [webhooks, setWebhooks] = useState<IWebhook[]>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [response, setResponse] = useState<AxiosResponse>()
  const [openWebhookForm, setOpenWebhookForm] = useState<IWebhook>()
  const [pingingWebhook, setPingingWebhook] = useState<number>()
  const [requestedDeleteFor, setRequestedDeleteFor] = useState<number>()
  const [isDeletingWebhook, setIsDeletingWebhook]=useState<number>()
  const [isTogglingActivation, setIsTogglingActivation] = useState<number>()
  const [showSecretIntroDialog, setShowSecretIntroDialog] = useState<string>()

  const disableAll = [isDeletingWebhook  ,isTogglingActivation , pingingWebhook].some(item => item!=undefined)
  useEffect(()=>{
    const fetchWebhooks = async () => {
        const endpoint = `${API_ENDPOINT}/v1/webhooks/`
        setIsLoading(true);
        try {
            setWebhooks((await axios.get<IWebhook[]>(endpoint, config)).data)
        } catch(e) {
            if (isAxiosError(e)) {
                setResponse(e.response)
            }

            showMessage('Failed to load webhooks', 'error')
        } finally {
            setIsLoading(false)
        }
    }
    fetchWebhooks()
  }, [])

  const handlePingWebhook = async (id: number) => {
    setPingingWebhook(id)
    try {
        await axios.post(`${API_ENDPOINT}/v1/webhooks/${id}/ping/`, {}, config)
        showMessage("Pong! All is good!")
    } catch {
        showMessage("Failed to ping your webhook", 'error')
    } finally {
        setPingingWebhook(undefined)
    }
  }

  const handleDelete = async (id: number) => {
    if (!webhooks) return
    setIsLoading(true)
    setResponse(undefined)
    setIsDeletingWebhook(id)
    try {
        await axios.delete(`${API_ENDPOINT}/v1/webhooks/${id}/`, config)
        showMessage("Webhook deleted.")
        setWebhooks(webhooks.filter(item => item.id != id))
    } catch(e) {

        if (isAxiosError(e)) 
            setResponse(e.response)

        showMessage("Failed to delete webhook", 'error')
    } finally {
        setIsLoading(false)
        setIsDeletingWebhook(undefined)
    }
  }

  const handleToggleActivation = async (w: IWebhook) => {
    if (!webhooks) return
    setResponse(undefined)
    setIsTogglingActivation(w.id)
    try {
        await axios.patch(`${API_ENDPOINT}/v1/webhooks/${w.id}/`, {is_active: !w.is_active},config)
        showMessage(`Webhook ${w.is_active ? 'Deactivated' : 'Activated'}.`)
        setWebhooks(webhooks.map(item => item.id != w.id ? item : {...item, is_active: !item.is_active}))
    } catch(e) {
        if (isAxiosError(e)) 
            setResponse(e.response)

        showMessage(`Failed to ${w.is_active ? 'Activate' : 'Deactivate'} webhook`, 'error')
    } finally {
        setIsLoading(false)
        setIsTogglingActivation(undefined)
    }
  }

    return <div>
        <div
            style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
            }}    
        >
            <div onClick={()=> setOpenWebhookForm(emptyWebhook) } style={{cursor: "pointer"}}>
                <IconWithText centered><AddLink  fontSize='large' color='primary'/> Register New</IconWithText>
            </div>
        </div>

         {response && response.status >= 300 && (
                    <Alert sx={{mb:4}} severity="error" >
                        Failed to save webhook
                        <ErrorsFlattenner rawErrors={response.data} />
                    </Alert>      
                  )}
        
        <GenericTable 
            sourceView="webhooks-listing"
            items={webhooks ?? []} 
            selectedItems={new Map} 
            isLoading={isLoading} 
            fullWidth
            columns={[
                {
                    id: "id",
                    header: "ID",
                    display_body: (w: IWebhook) => <div style={{textDecoration: "underline", cursor: "pointer"}} onClick={()=>setOpenWebhookForm(w)}><b>{w.id}</b></div>
                },
                {
                    id: "name",
                    header: "Name",
                    display_body: (w: IWebhook) => w.name
                },
                {
                    id: "endpoint",
                    header: "Endpoint",
                    display_body: (w: IWebhook) => w.endpoint,
                },
                {
                    id: "activity",
                    header: "Status",
                    display_body: (w: IWebhook) => <>
                            <Chip label= {w.is_active ? "Active" : "Inactive"} icon={w.is_active ? <CheckCircle fontSize='small'/> : <Cancel fontSize='small' />} sx={{background:w.is_active ? green[200] : red[100]}} />
                    </>
                },
                {
                    id: "toggle_activity",
                    header: "Turn on/off",
                    display_body: (w) => <Button disabled={disableAll} onClick={()=>handleToggleActivation(w)}>{w.is_active ? 'Deactivate' : 'Activate'} {isTogglingActivation == w.id && <CircularProgress sx={{ml: 3}} size={20}/>}</Button> 
                },
                {
                    id: "ping",
                    header: "Ping",
                    display_body: (w: IWebhook) => <Button disabled={disableAll} onClick={()=>handlePingWebhook(w.id)}>Ping <>{pingingWebhook == w.id && <CircularProgress sx={{ml: 3}} size={20}/>} </></Button>
                },
                {
                    id: "delete",
                    header: "Delete",
                    display_body: (w: IWebhook) => <Button onClick={()=>setRequestedDeleteFor(w.id)} disabled={disableAll} color='error'> Delete  <>{isDeletingWebhook == w.id && <CircularProgress sx={{ml: 3}} size={20}/>} </> </Button>
                }
            ]} 
            getItemUID={(w)=>w.id.toString()} 
            />
            {(requestedDeleteFor !=undefined) && <ConfirmAction 
                    message={'Are you sure you would like to delete this webhook?'} 
                    action={() => {setRequestedDeleteFor(undefined); handleDelete(requestedDeleteFor)}} 
                    isOpen={requestedDeleteFor != undefined} 
                    handleClose={() =>setRequestedDeleteFor(undefined)}
                    isSevereAction
                    confirmSevereActionTarget={'Delete Webhook'}
                    > 
                        <Alert severity="warning"> This action can not be undone, but you can register your webhook again at any time.</Alert>
                    </ConfirmAction>
            }

            {
                openWebhookForm && webhooks && <CustomDialog 
                    open={openWebhookForm != undefined} 
                    maxWidth={'md'} 
                    fullWidth 
                    fullScreen={(isMobile && !isTablet)}
                    onClose={()=>setOpenWebhookForm(undefined)}
                >

                    <DialogTitle> 
                        <Typography variant='h3' align='center'>
                            {openWebhookForm.id == 0 ? "New Webhook" : "Modify Webhook"} 
                        </Typography>
                    </DialogTitle>
                    
                    <DialogContent>
                        <WebhookForm defaultWebhook={openWebhookForm} onSuccess={(w)=> {
                            showMessage(`Webhook ${w.name} Saved Successfully`)
                            if (webhooks.some((webhook: IWebhook) => webhook.id == w.id)) {
                                setWebhooks(webhooks.map(item => item.id == w.id ? w : item))
                            } else {
                                setWebhooks([...webhooks, w])
                            } 
                            setShowSecretIntroDialog(w.secret)
                            setOpenWebhookForm(undefined)
                        }} />
                    </DialogContent>
                </CustomDialog> 
            }

            {
                showSecretIntroDialog &&
                <CustomDialog open={true} onClose={() => setShowSecretIntroDialog(undefined)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <Typography variant="h4" align="center" gutterBottom>
                            🔐 Your Webhook Secret
                        </Typography>
                    </DialogTitle>

                    <DialogContent>
                        <Alert sx={{ mt: 2, mb: 3 }} severity="warning">
                            <strong>Important:</strong> This secret key has been automatically downloaded to your device as a text file.
                            Please store it securely. You will not be able to view or retrieve it again after closing this dialog.
                        </Alert>

                        <Typography paragraph>
                            All webhook payloads will be signed using this secret key. To verify that requests are genuinely from <strong>ParcelTracer</strong>, you should validate the signature on your server.
                        </Typography>

                        <Typography paragraph>
                            For more details on verifying webhook signatures, please visit our{" "}
                            <a href="https://yourdocumentation.url/webhooks" target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", textDecoration: "underline" }}>
                                Webhook Security Documentation
                            </a>.
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 4 }}>
                            Your Secret Key:
                        </Typography>

                        <div style={{
                            padding: '12px 16px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            wordBreak: 'break-all',
                            fontFamily: 'monospace',
                            fontSize: '16px',
                            marginBottom: '16px',
                            textAlign: 'center'
                        }}>
                            {showSecretIntroDialog}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Button variant="outlined" onClick={() => {
                                navigator.clipboard.writeText(showSecretIntroDialog);
                                showMessage("Secret copied to clipboard");
                            }}>
                                Copy to Clipboard
                            </Button>
                        </div>
                    </DialogContent>
                </CustomDialog>
            }



                   
    </div>
}

export default WebhooksListing;
